// public/v3/src/components/time-entries-modal.jsx
//
// Per-patient time-entries modal opened from the Time Logs row "›" button.
// Lists every activityLog entry for the active month with inline Edit (opens
// TimeEntryEditModal) and Delete (PUT-back with entry removed). Mirrors the
// Time log tab inside Studio's preview pane — same data shape, same actions,
// but as a modal so users don't have to leave Time Logs to fix an entry.
//
// Props:
//   patient   — adapted patient row (must have ._raw for PUT-back)
//   month     — YYYY-MM key for the active month
//   onClose   — close handler
//   onSaved(nextData) — called after any successful mutation so the caller
//                       can refresh the parent row + monthly totals.

import React, { useEffect, useState } from 'react';
import { ccmApi } from '../lib/api';
import { entryDateForMonth } from '../lib/month';
import { entriesForMonth } from '../lib/activity-log';
import { TimeEntryEditModal } from './time-entry-edit-modal.jsx';

function entryMinutes(e) {
  return Number(e?.mins) || Number(e?.duration) || 0;
}

function staffNameForUser(user) {
  return String(user?.displayName || user?.name || user?.email || '').trim();
}

export function TimeEntriesModal({ patient, month, currentUser, onClose, onSaved }) {
  const [editingEntry, setEditingEntry] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // ESC closes — but only when the inner edit modal isn't open (otherwise the
  // edit modal owns the Escape key).
  useEffect(() => {
    if (editingEntry) return;
    const h = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [editingEntry, onClose]);

  if (!patient) return null;

  // entriesForMonth drops consolidated synthetic entries — important here
  // because users could otherwise edit/delete the consolidated note and break
  // the Elation chart-audit pairing.
  const log = Array.isArray(patient._raw?.activityLog) ? patient._raw.activityLog : [];
  const monthEntries = entriesForMonth(log, month)
    .sort((a, b) => (b.date || '').localeCompare(a.date || '')); // newest first

  const totalMins = monthEntries.reduce((s, e) => s + entryMinutes(e), 0);

  // Preserves _updatedAt on the body so the server's 409 optimistic-lock fires
  // on stale writes. Captures the response's new updatedAt before onSaved so
  // the parent (Time Logs page) carries the fresh timestamp on _raw for the
  // next mutation.
  async function deleteEntry(entry) {
    if (!entry || !patient._raw) return;
    if (!window.confirm(`Delete this ${entryMinutes(entry)}-min entry? This cannot be undone.`)) return;
    setBusy(true);
    setError(null);
    try {
      const nextLog = log.filter(e => e && e.id !== entry.id);
      const nextData = { ...patient._raw, activityLog: nextLog };
      const resp = await ccmApi.updatePatient(patient.id, nextData);
      const fresh = resp && resp.updatedAt ? resp.updatedAt : null;
      onSaved?.(fresh ? { ...nextData, _updatedAt: fresh } : nextData);
    } catch (e) {
      if (e && e.status === 409) {
        setError('Patient was updated elsewhere. Refresh and try again.');
      } else {
        setError(e?.message || 'Delete failed');
      }
    } finally {
      setBusy(false);
    }
  }

  function handleAdd() {
    const staff = staffNameForUser(currentUser);
    setEditingEntry({
      id: 'r' + Date.now(),
      pid: patient.id,
      date: entryDateForMonth(month),
      duration: 10,
      desc: '',
      label: '',
      notes: '',
      type: 'Other',
      kind: 'manual',
      ts: Date.now(),
      staff,
      staffUserId: currentUser?.id || '',
      _isNew: true,
    });
  }

  // The TimeEntryEditModal renders its own backdrop, so we hide ours while
  // it's open to prevent stacked dimming.
  return (
    <>
      {!editingEntry && (
        <div className="modal-backdrop" onClick={onClose}>
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-label={`Time entries for ${patient.name || patient._raw?.fname || patient.id}`}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 720 }}>
            <div className="modal-head">
              <div>
                <h3>Time entries</h3>
                <div className="sub">
                  {patient.name || patient._raw?.fname || patient.id}
                  {' · '}
                  <span style={{ color: 'var(--ink-3)' }}>
                    {monthEntries.length} entr{monthEntries.length === 1 ? 'y' : 'ies'} · {totalMins} min total
                  </span>
                </div>
              </div>
              <button className="btn ghost icon" onClick={onClose} aria-label="Close">✕</button>
            </div>
            <div className="modal-body" style={{ paddingTop: 8 }}>
              {monthEntries.length === 0 ? (
                <div style={{
                  padding: '36px 20px', textAlign: 'center', color: 'var(--ink-3)',
                  fontSize: 13, background: 'var(--surface-2)', borderRadius: 'var(--r)',
                }}>
                  No time logged this month. Use <b>+ Add entry</b> below to log one.
                </div>
              ) : (
                <div className="timelog">
                  {monthEntries.map((t) => (
                    <div className="timelog-row" key={t.id || `${t.date}-${t.ts}`}>
                      <span className="date">{t.date}</span>
                      <div>
                        <div className="kind">{t.kind || 'manual'}</div>
                        <div className="desc">{t.desc || t.label || ''}</div>
                      </div>
                      <span className="src">{t.src === 'voice' ? '🎙 voice-AI' : '⌨ manual'}</span>
                      <span className="mins tabnum">{entryMinutes(t)}m</span>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          className="btn ghost sm"
                          style={{ padding: '3px 8px' }}
                          disabled={busy}
                          onClick={() => setEditingEntry(t)}>
                          Edit
                        </button>
                        <button
                          className="btn ghost sm"
                          style={{ padding: '3px 8px', color: 'var(--bad)' }}
                          disabled={busy}
                          onClick={() => deleteEntry(t)}>
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {error && (
                <div style={{ color: 'var(--bad)', fontSize: 13, marginTop: 10 }}>{error}</div>
              )}
            </div>
            <div className="modal-foot">
              <button className="btn sm" onClick={handleAdd} disabled={busy}>+ Add entry</button>
              <button className="btn" onClick={onClose} disabled={busy}>Close</button>
            </div>
          </div>
        </div>
      )}

      {editingEntry && (
        <TimeEntryEditModal
          patient={patient}
          entry={editingEntry}
          currentUser={currentUser}
          onClose={() => setEditingEntry(null)}
          onSaved={(nextData) => {
            setEditingEntry(null);
            onSaved?.(nextData);
          }}
        />
      )}
    </>
  );
}
